// The job's held-back changes in one list, opened from the sync bar's "N held back" (#693;
// docs/mockups/held-back-changes.html): records of any kind someone else saved in CRM after
// this device loaded them. Each row says what it is, where, your change against CRM's now,
// and Keep mine / Keep CRM's (ConflictButtons, the area's own choice). Areas and buildings
// keep their choice on the job page too. Keep mine only queues the device's version, so its
// line says what the outbox has done with it since (held-back-outcome.ts), never "sent" before CRM took it.
import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@higgins/ui';
import { isConnectionFailure } from '../lib/api';
import { whyNotMine, type CrmVersion, type FieldChange } from '../lib/conflict-diff';
import { listHeldBack, loadCrmVersions } from '../lib/conflicts';
import { getJob, listOutbox, listRecords, type DeviceRecord } from '../lib/device-db';
import { sendOutcome, type SendOutcome } from '../lib/held-back-outcome';
import { shortError } from '../lib/short-error';
import { TABLE_NAMES } from '../lib/field-labels';
import { useDeviceQuery, useOnline } from '../lib/hooks';
import { ConflictButtons, type Choice } from './conflict-choices';

const LIST_LABELS: Record<Choice, string> = { mine: 'Keep mine', crm: "Keep CRM's" };

/** CRM's copies, once read: one read of the job for the whole list. */
type CrmState = { kind: 'loaded'; versions: Map<string, CrmVersion> } | { kind: 'failed'; reason: string };

interface Settled {
  id: string;
  title: string;
  choice: Choice;
}

function nameOf(record: DeviceRecord): string {
  const { hig_questionasked: question, hig_name: name } = record.fields;
  return String(question || name || TABLE_NAMES[record.table]);
}

/** "Option" → "option" for the question; "REVIEW answer" stays as it is. */
function kindInText(record: DeviceRecord): string {
  const kind = TABLE_NAMES[record.table];
  return /^[A-Z][a-z]/.test(kind) ? kind[0].toLowerCase() + kind.slice(1) : kind;
}

type Tone = 'done' | 'busy' | 'problem';

/** What a settled row's line says: CRM's version is taken there and then; mine, as far as the outbox has got. */
function settledLine(choice: Choice, outcome: SendOutcome, online: boolean): { tone: Tone; text: string } {
  if (choice === 'crm') return { tone: 'done', text: 'this device now shows CRM’s version.' };
  switch (outcome.kind) {
    case 'sent':
      return { tone: 'done', text: 'sent — CRM now has your version.' };
    case 'sending':
      return online ? { tone: 'busy', text: 'sending your version to CRM…' } : { tone: 'busy', text: 'your version waits for signal, then sends.' };
    case 'retrying':
      return { tone: 'problem', text: `not sent yet: ${shortError(outcome.error)} It is tried again by itself.` };
    case 'held':
      return { tone: 'problem', text: 'not sent: someone changed it in CRM again. Choose again below.' };
    case 'refused':
      return { tone: 'problem', text: `not sent: ${shortError(outcome.reason)} It is set aside on the sync bar (Try again or Discard).` };
    case 'gone':
      return { tone: 'busy', text: 'no longer on this device.' };
  }
}

const TONE_STYLE: Record<Tone, string> = {
  done: 'border-green-200 bg-green-50 text-green-800',
  busy: 'border-slate-200 bg-slate-50 text-slate-700',
  problem: 'border-amber-300 bg-amber-50 text-amber-900',
};
const TONE_ICON: Record<Tone, typeof CheckCircle2> = { done: CheckCircle2, busy: Loader2, problem: AlertTriangle };

function SettledLine({ title, choice, outcome, online }: { title: string; choice: Choice; outcome: SendOutcome; online: boolean }) {
  const { tone, text } = settledLine(choice, outcome, online);
  const Icon = TONE_ICON[tone];
  return (
    <p className={`mt-2 flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${TONE_STYLE[tone]}`} role="status">
      <Icon className={`h-4 w-4 shrink-0 ${tone === 'busy' && outcome.kind === 'sending' && online ? 'animate-spin' : ''}`} />
      <span>
        {title}: {text}
      </span>
    </p>
  );
}

function Comparison({ changes }: { changes: FieldChange[] }) {
  if (!changes.length) return <p className="mt-1 text-xs text-slate-500">CRM&rsquo;s copy now matches yours: either choice keeps the same.</p>;
  return (
    <div className="mt-2 grid grid-cols-[7rem_minmax(0,15rem)_minmax(0,15rem)] gap-x-3 gap-y-0.5 text-sm">
      <span />
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Your change</span>
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">CRM now</span>
      {changes.map(({ label, shown }) => (
        <div key={label} className="contents">
          <span className="text-slate-600">{label}</span>
          {shown ? (
            <>
              <span className="font-medium text-slate-900">{shown.mine}</span>
              <span className="text-slate-900">{shown.crm}</span>
            </>
          ) : (
            <span className="col-span-2 text-slate-700">differs</span>
          )}
        </div>
      ))}
    </div>
  );
}

function RowCompare({ version, crm, online }: { version: CrmVersion | undefined; crm: CrmState | null; online: boolean }) {
  if (version?.kind === 'changed') return <Comparison changes={version.changes} />;
  if (version?.kind === 'removed') return <p className="mt-1 text-sm text-slate-700">Someone removed this in CRM.</p>;
  const text = !online ? 'Connect to see what CRM has now.' : crm?.kind === 'failed' ? `Couldn’t read CRM’s copy: ${crm.reason}` : 'Checking what CRM has…';
  return <p className="mt-1 text-xs text-slate-500">{text}</p>;
}

interface Props {
  jobId: string;
  onClose: () => void;
}

export function HeldBackList({ jobId, onClose }: Props) {
  const online = useOnline();
  const view = useDeviceQuery(async () => {
    const [held, records, job, outbox] = await Promise.all([listHeldBack(jobId), listRecords(jobId), getJob(jobId), listOutbox()]);
    const names = new Map(records.map((r) => [r.id, nameOf(r)]));
    const byId = new Map(records.map((r) => [r.id, r]));
    const queueErrors = new Map(outbox.filter((e) => e.kind === 'record' && e.lastError).map((e) => [e.targetId, e.lastError]));
    return { held, names, byId, queueErrors, jobNumber: job?.number ?? '' };
  }, [jobId]);
  const [crm, setCrm] = useState<CrmState | null>(null);
  const [settled, setSettled] = useState<Settled[]>([]);
  // Read CRM again whenever what's held back changes: a row settled, or another held back.
  const heldKey = view?.held.map((r) => `${r.id}:${r.revision}`).join(',') ?? '';

  useEffect(() => {
    if (!online || !heldKey) return;
    let current = true;
    listHeldBack(jobId)
      .then((held) => loadCrmVersions(jobId, held))
      .then((versions) => current && setCrm({ kind: 'loaded', versions }))
      .catch((err: unknown) => {
        const reason = isConnectionFailure(err) ? 'this needs signal.' : err instanceof Error ? err.message : String(err);
        if (current) setCrm({ kind: 'failed', reason });
      });
    return () => {
      current = false;
    };
  }, [jobId, heldKey, online]);

  if (!view) return null;
  const { held, names, byId, queueErrors, jobNumber } = view;
  return (
    <section className="mb-3 rounded-lg border border-slate-200 bg-white/95 p-3" aria-label="Held-back changes">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={onClose}>
          ‹ Back
        </Button>
        <h2 className="text-base font-semibold">Held back ({held.length})</h2>
      </div>
      <p className="mt-1 max-w-prose text-sm text-slate-600">
        Someone else saved these records in CRM after you started. Your changes are kept on this device and not sent until you choose.{' '}
        <strong>Keep mine</strong> sends yours over theirs; <strong>Keep CRM&rsquo;s</strong> drops yours.
      </p>
      {settled.map(({ id, title, choice }) => (
        <SettledLine key={id} title={title} choice={choice} outcome={sendOutcome(byId.get(id), queueErrors.get(id) ?? null)} online={online} />
      ))}
      {held.length === 0 && <p className="mt-3 text-sm text-slate-600">Nothing held back. Every change on this device has been sent or settled.</p>}
      {held.map((record) => {
        const version = crm?.kind === 'loaded' ? crm.versions.get(record.id) : undefined;
        const notMine = version ? whyNotMine(record.table, version) : null;
        const name = nameOf(record);
        const parent = names.get(record.parentId);
        return (
          <article key={record.id} className="mt-3 rounded-lg border border-l-4 border-slate-200 border-l-amber-600 bg-white px-3 py-2">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="rounded bg-slate-100 px-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{TABLE_NAMES[record.table]}</span>
              <span className="font-semibold">{name}</span>
              <span className="text-xs text-slate-500">
                {jobNumber}
                {parent && ` · in ${parent}`}
              </span>
            </div>
            <RowCompare version={version} crm={crm} online={online} />
            {notMine && version?.kind === 'changed' && <p className="mt-1 text-xs text-slate-600">{notMine}</p>}
            <ConflictButtons
              record={record}
              what={kindInText(record)}
              name={name}
              labels={LIST_LABELS}
              mineOffered={!notMine}
              onDone={(choice) => setSettled((done) => [...done.filter((d) => d.id !== record.id), { id: record.id, title: name, choice }])}
            />
          </article>
        );
      })}
    </section>
  );
}
